<script setup>
import { reactive, onMounted, computed, watch, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import LeftMenuWrapper from '@/Components/LeftMenu/LeftMenuWrapper.vue';
import axios from 'axios';
import { updateQueryParamsAndReload } from '@/Components/FilterManager.vue';
import TableViewTable from '@/Components/Product/TableViewTable.vue';
import BreadcrumbBar from '@/Components/BreadcrumbBar.vue';
import PriceCompareLayout from '../../Layouts/PriceCompareLayout.vue';

const page = usePage();

const user = computed(() => page.props.auth.user);
const breadcrumbs = computed(() => page.props.breadcrumbs);
const pageTitle = computed(() => page.props.pageTitle);
const search = computed(() => page.props.search);

// Add reactive seoData object initialized from server props
const initialSeoData = computed(() => page.props.seo || {});
const seoData = reactive({
    title: initialSeoData.value?.title || '',
    description: initialSeoData.value?.description || '',
    keywords: initialSeoData.value?.keywords || '',
    robots: 'index, follow', // Default robots value
    openGraph: initialSeoData.value?.openGraph || {},
    structuredData: initialSeoData.value?.structuredData || []
});

// Watch for seoData changes to update document head
watch(seoData, (newSeoData) => {
    updateDocumentHead(newSeoData);
}, { deep: true });

// Function to update document head elements
function updateDocumentHead(seoData) {
    // Update page title
    document.title = seoData.title;
    
    // Update or create meta tags
    updateMetaTag('description', seoData.description);
    updateMetaTag('keywords', seoData.keywords);
    updateMetaTag('robots', seoData.robots);
    
    // Update OpenGraph meta tags
    if (seoData.openGraph) {
        updateMetaTag('og:title', seoData.openGraph.title);
        updateMetaTag('og:description', seoData.openGraph.description);
        updateMetaTag('og:image', seoData.openGraph.image);
        updateMetaTag('og:type', seoData.openGraph.type);
    }
}

// Helper function to update or create meta tag
function updateMetaTag(name, content) {
    if (!content) return;
    
    // Check if tag exists (either by name or property attribute)
    let metaTag = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
    
    if (metaTag) {
        metaTag.setAttribute('content', content);
    } else {
        metaTag = document.createElement('meta');
        // Determine if we should use name or property attribute (OG tags use property)
        if (name.startsWith('og:')) {
            metaTag.setAttribute('property', name);
        } else {
            metaTag.setAttribute('name', name);
        }
        metaTag.setAttribute('content', content);
        document.head.appendChild(metaTag);
    }
}

const usedFilters = reactive({
    filters: [],
});

// Replace queryParameters initialization with $page.props.query
const queryParameters = ref(new URLSearchParams(page.props.query));

// Update watch to use $page.props.query instead of window.location.search
watch(() => page.props.query, (newQuery) => {
    queryParameters.value = new URLSearchParams(newQuery);
}, { immediate: true });

const state = reactive({
    products: [],
    loading: false,
    page: 1,
    perPage: 50,
    currentSortKey: 'NEWEST',
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
});

onMounted(async () => {
    await getProducts();
    // Initialize document head with initial SEO data
    updateDocumentHead(seoData);
});

async function onFilterUpdated(filters) {
    usedFilters.filters = filters;

    // Update URL with new filters
    updateQueryParamsAndReload(filters);

    // Update queryParameters with $page.props.query
    queryParameters.value = new URLSearchParams(page.props.query);

    await getProducts(1);
}

const emit = defineEmits(['sortingUpdated']);

async function onSortingUpdated(sorting) {
    state.currentSortKey = sorting;

    updateSortingInUrl(sorting);

    await getProducts(1);
}

const categoryId = page.props.subSubCategory.id;

// Update getProducts function to use queryParameters safely
async function getProducts(showpage = 1) {
    try {
        state.loading = true;

        // Extract active filters from queryParameters
        const activeFilters = {};
        for (const [key, value] of queryParameters.value.entries()) {
            if (key !== 'sort' && key !== 'page') {
                const decodedValue = decodeURIComponent(value);
                activeFilters[key] = decodedValue;
            }
        }

        const filterString = Object.entries(activeFilters)
            .map(([key, value]) => `${key}:${value}`)
            .join('|');

        const sortFromUrl = queryParameters.value.get('sort');
        const sort = sortFromUrl ? sortFromUrl.toUpperCase() : state.currentSortKey;

        state.currentSortKey = sort;

        // Update page number in URL
        updatePageInUrl(showpage);

        const response = await axios.get(`/api/v1/products/${categoryId}/`, {
            params: {
                page: showpage,
                per_page: state.perPage,
                filters: filterString,
                sort: sort
            }
        });

        const data = response.data;

        state.products = data.products;
        state.page = data.pagination.current_page;
        state.totalPages = data.pagination.total_pages;
        state.totalItems = data.pagination.total_items;
        state.hasNextPage = data.pagination.has_next_page;
        state.hasPrevPage = data.pagination.has_prev_page;
        state.currentSortKey = sort;

        // Update SEO data based on API response if available
        if (data.seoData) {
            // Update SEO data with response data
            seoData.title = data.seoData.title || seoData.title;
            seoData.description = data.seoData.description || seoData.description;
            seoData.keywords = data.seoData.keywords || seoData.keywords;
            seoData.robots = data.seoData.robots || 'index, follow';
            
            if (data.seoData.openGraph) {
                seoData.openGraph = {...seoData.openGraph, ...data.seoData.openGraph};
            }
            
            if (data.seoData.structuredData) {
                seoData.structuredData = data.seoData.structuredData;
            }
        }

        state.loading = false;
    } catch (error) {
        console.error('Error fetching products:', error);
        state.loading = false;
    }
}

// Ensure sort parameter is always last in URL and lowercase
function updateSortingInUrl(sortKey) {
    const searchParams = new URLSearchParams(window.location.search);
    
    // Store current sort value (ensure uppercase internally)
    const currentSort = sortKey || searchParams.get('sort') || state.currentSortKey;
    
    // Remove sort from current params (to add it back at the end)
    searchParams.delete('sort');
    
    // Add sort as the last parameter (convert to lowercase for URL)
    searchParams.append('sort', currentSort.toLowerCase());
    
    // Update URL without reloading the page
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    
    // Also update our reactive queryParameters
    queryParameters.value = new URLSearchParams(window.location.search);
}

// Function to update page parameter in URL
function updatePageInUrl(page) {
    const searchParams = new URLSearchParams(window.location.search);
    
    if (page === 1) {
        // Remove page parameter for first page to keep URL clean
        searchParams.delete('page');
    } else {
        // Set page parameter for other pages
        searchParams.set('page', page.toString());
    }
    
    // Update URL without reloading the page
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    
    // Also update our reactive queryParameters
    queryParameters.value = new URLSearchParams(window.location.search);
}

function getQueryString(queryParameters) {
    const filters = {};

    for (const [key, value] of queryParameters.entries()) {
        if (value.trim() !== '') { // Remove empty values
            filters[key] = filters[key] ? `${filters[key]},${value}` : value;
        }
    }

    return Object.entries(filters)
        .map(([key, value]) => `${key}:${value}`)
        .join('|');
}

async function loadPrevPage() {
    if (state.page > 1) {
        await getProducts(state.page - 1);
    }
}

async function loadNextPage() {
    if (state.hasNextPage) {
        await getProducts(state.page + 1);
    }
}

async function loadSpecificPage(page) {
    if (page >= 1 && page <= state.totalPages && page !== state.page) {
        await getProducts(page);
    }
}

async function loadMore() {
    state.page++;
    await getProducts();
}

</script>

<template>
    <PriceCompareLayout>
        <div class="container-fluid text-center g-0">
            <div class="breadcrumb-spacing">
                <BreadcrumbBar :breadcrumbs="breadcrumbs" />
            </div>
            <div class="row g-0">
                <!-- Left menu column -->
                <div class="col-sm-12 col-md-3 col-lg-2 float-modal-mobile ps-md-3">
                    <LeftMenuWrapper @filterUpdated="onFilterUpdated" :categoryId="categoryId" :queryParameters="queryParameters" />
                </div>
                <!-- Main content column -->
                <div class="col main-content-block">
                    <div class="card rounded-0 mb-4 shadow-sm">
                        <div class="card-body p-0">
                            <div class="container-fluid">
                                <div class="row">
                                    <div v-if="state.loading" class="spinner-border text-primary m-5" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                    <TableViewTable 
                                        v-if="!state.loading && state.products.length > 0" 
                                        :products="state.products" 
                                        :loadNextPage="loadNextPage" 
                                        :loadPrevPage="loadPrevPage" 
                                        :loadSpecificPage="loadSpecificPage" 
                                        :currentPage="state.page"
                                        :totalPages="state.totalPages"
                                        @sortingUpdated="onSortingUpdated" 
                                        :selectedSort="state.currentSortKey" 
                                    />
                                    <div v-else-if="!state.loading && state.products.length === 0" class="text-center p-5">
                                        <p>No products found.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="user-info-card card mb-4 shadow-sm">
                        <div class="card-body">
                            {{ user ? user.name : 'Guest' }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </PriceCompareLayout>
</template>

<style scoped>
.main-content-block {
    padding-left: 15px;
    padding-right: 15px;
}

.card {
    border-radius: 0.5rem;
    border: none;
    overflow: hidden;
    transition: transform 0.2s, box-shadow 0.2s;
}

.card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.user-info-card {
    background-color: white;
    border-radius: 0.5rem;
}

.shadow-sm {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

/* Mobile styling */
.float-modal-mobile {
    position: relative;
    z-index: 1000;
}

@media (max-width: 767.98px) {
    .main-content-block {
        padding-left: 10px;
        padding-right: 10px;
        margin-top: 15px;
    }
}

@media (min-width: 768px) {
    .float-modal-mobile {
        position: sticky;
        top: 20px;
    }
    .breadcrumb-spacing {
        margin-bottom: 20px;
    }
}
</style>